import { computed, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';

export interface User {
  email: string;
  fullName: string;
  phoneNumber: string;
  address: string;
  isBusinessUser: boolean;
  isAdmin: boolean;
}

export interface ActingAsCustomer {
  id: string;
  name: string;
  companyName?: string;
}

const TOKEN_KEY = 'rp_token';
const ADMIN_TOKEN_KEY = 'rp_admin_token';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly cookies = inject(CookieService);

  readonly token = signal<string | null>(this.cookies.get(TOKEN_KEY) || null);
  readonly currentUser = signal<User | null>(null);
  readonly actingAsCustomer = signal<ActingAsCustomer | null>(
    this._loadActingAs()
  );

  readonly switching$ = new BehaviorSubject<boolean>(false);
  readonly userSwitched$ = new Subject<void>();

  readonly isAuthenticated = computed(() => !!this.token());
  readonly isBusinessUser = computed(() => !!this.currentUser()?.isBusinessUser);
  readonly isAdmin = computed(() => !!this.currentUser()?.isAdmin);

  setToken(token: string): void {
    this.cookies.set(TOKEN_KEY, token, { path: '/' });
    this.token.set(token);
  }

  setUser(user: User): void {
    this.currentUser.set(user);
  }

  switchToCustomer(token: string, customer: ActingAsCustomer): void {
    const adminToken = this.cookies.get(TOKEN_KEY);
    if (adminToken) {
      this.cookies.set(ADMIN_TOKEN_KEY, adminToken, { path: '/' });
    }
    this.cookies.set(TOKEN_KEY, token, { path: '/' });
    this.token.set(token);
    this.actingAsCustomer.set(customer);
    sessionStorage.setItem('rp_acting_as', JSON.stringify(customer));
    this.userSwitched$.next();
  }

  restoreToAdmin(): void {
    const adminToken = this.cookies.get(ADMIN_TOKEN_KEY);
    if (adminToken) {
      this.cookies.set(TOKEN_KEY, adminToken, { path: '/' });
      this.cookies.delete(ADMIN_TOKEN_KEY, '/');
      this.token.set(adminToken);
    }
    this.actingAsCustomer.set(null);
    sessionStorage.removeItem('rp_acting_as');
    this.userSwitched$.next();
  }

  logout(): void {
    this.cookies.delete(TOKEN_KEY, '/');
    this.cookies.delete(ADMIN_TOKEN_KEY, '/');
    this.token.set(null);
    this.currentUser.set(null);
    this.actingAsCustomer.set(null);
    sessionStorage.removeItem('rp_acting_as');
  }

  private _loadActingAs(): ActingAsCustomer | null {
    try {
      const raw = sessionStorage.getItem('rp_acting_as');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
}
